<script setup lang="ts">
import { computed, ref } from 'vue';
import { useClaimStore } from '@/store';

import BadgeLegalStep from '../badges/BadgeLegalStep.vue';
import { LinkIcon, ArrowRightIcon } from '@heroicons/vue/24/outline'
import AlertModal from './AlertModal.vue';

type AlertModalType = "info" | "warning";

const emits = defineEmits(['close', 'cancel', 'confirm']);

const props = defineProps<{
  open: boolean,
  event: string | null
}>();

const form = ref<{
  internalNote?: string;
  eventDate?: Date;
  messageLink?: string;
}>({ internalNote: undefined });

const currentLegalStep = computed(() => useClaimStore().claim.claimLegalStep.name);
const nextLegalStep = computed(() => {
  const {machineState}  = useClaimStore();
  const afterState = machineState.machine?.transition(machineState as any, props.event as string);
  return afterState?.context.LEGAL_STEP;
});
const legalStepWillChange = computed(() => currentLegalStep.value !== nextLegalStep.value);

function addEvent() {
  if (props.event) {
    const { internalNote, eventDate, messageLink } = form.value;
    useClaimStore().sendEvent(useClaimStore()?.claim.id, {
      machineEvent: props.event, // @ts-ignore
      messageLink: messageLink || null,
      internalNote,
      eventDate,
    });
    emits('confirm');
    form.value = {};
  }
}

function close() {
  form.value = {};
  emits("close");
}
</script>

<template>
  <AlertModal
    :open="open"
    title="Envoyer un évènement"
    message="Vous êtes sur le point d'ajouter un évènement à la claim.<br>Êtes-vous sûr de continuer ?"
    :type="legalStepWillChange ? `warning` : `info`"
    confirm-message="Envoyer"
    @cancel="close"
    @close="close"
    @confirm="addEvent"
  >
    <div
    class="p-4 mb-4 text-sm text-gray-800 border border-gray-200 rounded-lg bg-gray-50" role="alert">
      <div class="mb-3">
        <p class="mb-3 font-medium">Évènement:</p>
        <span class="px-3 py-1.5 text-xs font-semibold text-gray-900 bg-white border border-gray-200 rounded-full">
          {{ event }}
        </span>
      </div>

      <!-- <ExclamationCircleIcon class="flex-shrink-0 inline w-5 h-5 mr-3 text-red-700" /> -->
      <!-- <span class="sr-only">Info</span> -->
      <div v-if="nextLegalStep && legalStepWillChange" class="mt-8">
        <span class="font-bold">L'étape juridique du dossier va changer:</span>
          <div class="flex my-3">
            <BadgeLegalStep :name="currentLegalStep" rounded="rounded-md" class="" /> 
            <ArrowRightIcon class="w-5 h-5 mx-2 text-gray-500" />
            <BadgeLegalStep :name="nextLegalStep as any" rounded="rounded-md" class="mr-4" />
        </div>
      </div>
    </div>

    <div>
      <div class="my-6 ">
        <label for="eventDate" class="block text-sm font-medium text-gray-700">
        Date de l'évènement <span class="font-extrabold">*</span>
      </label>
        <VueDatePicker
          v-tooltip="`Date du dernier message reçu par défault`"
          v-model="form.eventDate"
          auto-apply
          :close-on-auto-apply="true"
          :max-date="new Date()"
          :start-time="{ hours: 8, minutes: 0 }"
          :end-time="{ hours: 19, minutes: 0 }"
          format="dd/MM/yyyy"
          text-input placeholder="Date de l'évènement"
          locale="fr" cancelText="Annuler" selectText="Valider"
        />
      </div>
      <div>
        <!-- We use message ID, and not message link, to preserve lawyer's confidentiality,
            since link includes a viewable permissions key -->
        <label for="messageLink" class="block text-sm font-medium text-gray-700">
        Message Link
      </label>
        <div class="relative mt-1 rounded-md shadow-sm">
          <div class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
            <LinkIcon class="w-5 h-5 text-gray-400" aria-hidden="true" />
          </div>
          <input v-model="form.messageLink" type="text" name="messageLink" id="messageLink"
            v-tooltip="`« Copy message ID » sur Front`"
            class="block w-full pl-10 border-gray-300 rounded-md focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            placeholder="https://"
          />
        </div>
      </div>
      <div class="mt-6">
        <label for="comment" class="block text-sm font-medium text-gray-700">Ajouter une note</label>
        <textarea :rows="4" name="comment" id="comment"
          v-model="form.internalNote"
          placeholder="Exemple : Un commentaire sur la réponse de la compagnie"
          class="block w-full border-gray-300 rounded-md shadow-sm focus:border-blue-600 focus:ring-blue-600 sm:text-sm"
        />
      </div>
    </div>
  </AlertModal>
</template>
